import { ReactComponent as Download } from 'src/assets/download.svg';
import Like from "./like"
import React, { useEffect, useState, useRef } from "react";
import { triggerConfetti } from 'src/hooks/useConfetti';
import { useIsMobile } from "../../../hooks/useIsMobile";
import { trackEvent } from '../../../lib/analytics';
import { ReactComponent as Instagram } from 'src/assets/sns/Instagram.svg';
import { ReactComponent as TwitterX } from 'src/assets/sns/XAuthor.svg';
import { ReactComponent as Branch } from 'src/assets/sns/branch.svg';
import { ReactComponent as Blog } from 'src/assets/sns/Internet.svg';
import { ReactComponent as Info } from 'src/assets/info.svg';
import Alert from "../../../common/layouts/modal/Alert";
import { useNavigate } from "react-router-dom";
import { downloadPostCard, getAuthorInfo } from "../../../api";

interface GoodsModalProps {
    imageSrc: string | null;
    postData: any;
    closeModal: () => void;
    onLikeToggle?: (newLiked: boolean, b: boolean) => void;
    onToggleLike: (postId: string) => void;
}

const SnsLinks = ({ postData }: { postData: any }) => (
    <div className="flex flex-row gap-3 items-center">
        {postData.xlink && (
            <a href={postData.xlink} target="_blank" rel="noopener noreferrer"
               className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f5f5] hover:bg-[#e8f8f0] transition-colors">
                <TwitterX className="w-4 h-4" />
            </a>
        )}
        {postData.instagramLink && (
            <a href={postData.instagramLink} target="_blank" rel="noopener noreferrer"
               className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f5f5] hover:bg-[#e8f8f0] transition-colors">
                <Instagram className="w-4 h-4" />
            </a>
        )}
        {postData.brunchLink && (
            <a href={postData.brunchLink} target="_blank" rel="noopener noreferrer"
               className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f5f5] hover:bg-[#e8f8f0] transition-colors">
                <Branch className="w-4 h-4" />
            </a>
        )}
        {postData.naverLink && (
            <a href={postData.naverLink} target="_blank" rel="noopener noreferrer"
               className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f5f5] hover:bg-[#e8f8f0] transition-colors">
                <Blog className="w-4 h-4" />
            </a>
        )}
    </div>
);

const GoodsModal = ({ imageSrc, postData, closeModal, onToggleLike }: GoodsModalProps) => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const viewedIdRef = useRef('');
    const [showAlert, setShowAlert] = useState(false);

    useEffect(() => {
        const currentId = String(postData?.postCardId || '');
        if (!currentId || viewedIdRef.current === currentId) return;
        viewedIdRef.current = currentId;
        trackEvent('open_postcard_modal', {
            postcard_id: currentId,
            page_path: window.location.pathname,
        });
    }, [postData?.postCardId]);

    const [authStatusState, setAuthStatusState] = useState(() => localStorage.getItem("authorStatus"));

    useEffect(() => {
        const handleLoginSuccess = () => {
            setAuthStatusState(localStorage.getItem("authorStatus"));
        };
        window.addEventListener('login_success', handleLoginSuccess);
        return () => window.removeEventListener('login_success', handleLoginSuccess);
    }, []);

    const moveToAuthor = async (name: string, email: string) => {
        try {
            const authorInfo = await getAuthorInfo({ authorEmail: email });
            if (authorInfo && authorInfo.authorId) {
                navigate(`/author/authordetails/${authorInfo.authorId}`, {
                    state: { authorEmail: email }
                });
            } else {
                navigate(`/author/authordetails/${name.replace(/\s+/g, '')}`, {
                    state: { authorEmail: email }
                });
            }
        } catch (error) {
            console.error("Failed to fetch authorInfo for navigation:", error);
            navigate(`/author/authordetails/${name.replace(/\s+/g, '')}`, {
                state: { authorEmail: email }
            });
        }
    };

    const handleDownload = async () => {
        if (!authStatusState) {
            setShowAlert(true);
            return;
        }

        try {
            const response = await downloadPostCard(postData.postCardId);
            
            // Content-Type에 따른 확장자 추출
            const contentType = response.headers['content-type'] || '';
            let ext = 'png';
            if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) {
                ext = 'jpg';
            } else if (contentType.includes('image/webp')) {
                ext = 'webp';
            } else if (contentType.includes('image/gif')) {
                ext = 'gif';
            }

            // 파일명 조립: bookmeet_작가명_postcard_id.확장자
            const authorName = postData.authorName || 'unknown';
            const postcardId = postData.postCardId || '0';
            const filename = `bookmeet_${authorName}_postcard_${postcardId}.${ext}`;

            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
            const dataUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = dataUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(dataUrl);

        } catch (error) {
            console.error("Failed to download postcard:", error);
            alert("엽서 다운로드에 실패했습니다.");
        }
    };

    const testYn = postData.testStatus;

    return (
        <>
            {isMobile ? (
                /* ── 모바일 레이아웃 ── */
                <div className="flex flex-col w-[320px] h-full">
                    {/* 헤더 */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#f0f0f0]">
                        <div className="flex items-center gap-2 cursor-pointer"
                             onClick={() => moveToAuthor(postData.authorName, postData.authorEmail)}>
                            <img
                                className="w-7 h-7 rounded-full object-cover"
                                src={postData.authorProfilePhoto
                                    ? `data:image/png;base64,${postData.authorProfilePhoto}`
                                    : "/img/profile_sam.jpg"}
                                alt="profile"
                            />
                            <span className="font-bold text-[14px] text-[#222]">{postData.authorName}</span>
                        </div>
                        <button onClick={closeModal} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                            <img className="w-4 h-4" src={`${process.env.PUBLIC_URL}/close.svg`} alt="닫기" />
                        </button>
                    </div>

                    {testYn && (
                        <div className="flex items-center gap-1.5 px-4 py-2 text-[#aaa] text-[11px] bg-[#fafafa]">
                            <Info className="w-3.5 h-3.5" />
                            <span>예시 데이터로 구성된 페이지입니다.</span>
                        </div>
                    )}

                    {/* 이미지 */}
                    <div className="flex justify-center px-4 py-4 bg-[#fafafa]">
                        {imageSrc ? (
                            <img
                                src={`data:image/png;base64,${imageSrc}`}
                                className="w-[200px] h-[253px] object-cover rounded-xl shadow-sm"
                                alt="엽서"
                            />
                        ) : (
                            <div className="w-[200px] h-[253px] bg-[#f0f0f0] rounded-xl flex items-center justify-center text-[#ccc]">
                                이미지 없음
                            </div>
                        )}
                    </div>

                    {/* 설명 + 액션 */}
                    <div className="flex flex-col gap-3 px-4 py-4 flex-1 min-h-0 overflow-hidden">
                        {postData.postCardDescription && (
                            <p
                                className="text-[13px] text-[#555] leading-relaxed overflow-y-auto max-h-[100px]"
                                style={{ wordBreak: 'break-all', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                            >
                                {postData.postCardDescription}
                            </p>
                        )}

                        <div className="flex items-center justify-between pt-1">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1"
                                     onMouseDown={(e) => {
                                         if (authStatusState && !postData.like) {
                                             triggerConfetti(e, { count: 20, spread: 90, originY: -10 });
                                         }
                                     }}>
                                    <Like onClick={() => onToggleLike(postData.postCardId)} liked={postData.like} />
                                    <span className="text-[13px] text-[#888]">{postData.likeCount}</span>
                                </div>
                                <button onClick={handleDownload}
                                        className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f5f5] hover:bg-[#e8f8f0] transition-colors">
                                    <Download className="w-4 h-4 text-[#555]" />
                                </button>
                            </div>
                            <SnsLinks postData={postData} />
                        </div>
                    </div>
                </div>

            ) : (
                /* ── 데스크탑 레이아웃 ── */
                <div className="flex flex-col w-[580px]">
                    {/* 헤더 */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-[#f0f0f0]">
                        <div className="flex items-center gap-2.5 cursor-pointer"
                             onClick={() => moveToAuthor(postData.authorName, postData.authorEmail)}>
                            <img
                                className="w-9 h-9 rounded-full object-cover shadow-sm"
                                src={postData.authorProfilePhoto
                                    ? `data:image/png;base64,${postData.authorProfilePhoto}`
                                    : "/img/profile_sam.jpg"}
                                alt="profile"
                            />
                            <div className="flex flex-col">
                                <span className="font-bold text-[15px] text-[#222] hover:text-[#19cf7a] transition-colors">
                                    {postData.authorName}
                                </span>
                                <span className="text-[12px] text-[#aaa]">작가 프로필 보기 →</span>
                            </div>
                        </div>
                        <button onClick={closeModal}
                                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                            <img className="w-4 h-4" src={`${process.env.PUBLIC_URL}/close.svg`} alt="닫기" />
                        </button>
                    </div>

                    {testYn && (
                        <div className="flex items-center gap-1.5 px-6 py-2 text-[#aaa] text-[11px] bg-[#fafafa] border-b border-[#f5f5f5]">
                            <Info className="w-3.5 h-3.5" />
                            <span>예시 데이터로 구성된 페이지입니다.</span>
                        </div>
                    )}

                    {/* 본문 */}
                    <div className="flex flex-row gap-5 p-6 min-h-0">
                        {/* 이미지 */}
                        <div className="flex-shrink-0">
                            {imageSrc ? (
                                <img
                                    src={`data:image/png;base64,${imageSrc}`}
                                    className="w-[180px] h-[227px] object-cover rounded-xl shadow-sm"
                                    alt="엽서"
                                />
                            ) : (
                                <div className="w-[180px] h-[227px] bg-[#f5f5f5] rounded-xl flex items-center justify-center text-[#ccc]">
                                    이미지 없음
                                </div>
                            )}
                        </div>

                        {/* 정보 */}
                        <div className="flex flex-col gap-4 flex-1 min-w-0 overflow-hidden justify-between">
                            {/* 설명 텍스트 - 스크롤 가능한 영역 */}
                            <div className="flex-1 overflow-y-auto min-h-0 max-h-[160px] pr-1">
                                {postData.postCardDescription ? (
                                    <p
                                        className="text-[14px] text-[#555] leading-relaxed"
                                        style={{ wordBreak: 'break-all', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}
                                    >
                                        {postData.postCardDescription}
                                    </p>
                                ) : (
                                    <p className="text-[14px] text-[#ccc] italic">설명이 없습니다.</p>
                                )}
                            </div>

                            <div className="flex flex-col gap-3 flex-shrink-0">
                                <div className="h-px bg-[#f0f0f0]" />

                                {/* 좋아요 + 다운로드 + SNS */}
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-1"
                                             onMouseDown={(e) => {
                                                 if (authStatusState && !postData.like) {
                                                     triggerConfetti(e, { count: 20, spread: 90, originY: -10 });
                                                 }
                                             }}>
                                            <Like onClick={() => onToggleLike(postData.postCardId)} liked={postData.like} />
                                            <span className="text-[13px] text-[#888]">{postData.likeCount}</span>
                                        </div>
                                        <button
                                            onClick={handleDownload}
                                            className="flex items-center gap-1 text-[13px] text-[#888] hover:text-[#19cf7a] transition-colors px-2.5 py-1.5 rounded-full hover:bg-[#e8f8f0]"
                                        >
                                            <Download className="w-4 h-4" />
                                            <span>다운로드</span>
                                        </button>
                                    </div>
                                    <SnsLinks postData={postData} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAlert && (
                <Alert
                    title="로그인이 필요해요"
                    message={"엽서를 다운로드하려면\n로그인이 필요합니다."}
                    onClose={() => setShowAlert(false)}
                    showLoginOnConfirm={true}
                />
            )}
        </>
    );
};

export default GoodsModal;
